import { ImageUploaderComponent, ValidImageResult } from '../../../Design/image-uploader/image-uploader.component';
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BrandService } from '../../../services/brand/brand.service';
import { CategoriesService, CategoryDto } from '../../../services/categories/categories.service';
import { SnackbarService } from '../../../services/design/snackbar.service';

type BrandStatus = 'draft' | 'active' | 'hidden';
type BrandType =
  | 'National Brand'
  | 'Regional Brand'
  | 'Local Brand'
  | 'Private Label';

type StepId =
  | 'basic'
  | 'media'
  | 'catalog'
  | 'publish';

interface StepDef {
  id: StepId;
  title: string;
  subtitle: string;
  icon: string;
}

// legacy UploadItem removed

const DRAFT_KEY = 'supidoo_admin:create_brand_draft:v1';

@Component({
  selector: 'app-create-brand',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ImageUploaderComponent],
  templateUrl: './create-brand.component.html',
  styleUrl: './create-brand.component.scss',
})
export class CreateBrandComponent {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly snackbar = inject(SnackbarService);
  private readonly brandService = inject(BrandService);
  private readonly categoriesService = inject(CategoriesService);
  private readonly destroyRef = inject(DestroyRef);

  readonly Math = Math;

  readonly steps: StepDef[] = [
    {
      id: 'basic',
      title: 'Basic Info',
      subtitle: 'Identity and status',
      icon: 'fa-solid fa-id-card',
    },
    {
      id: 'media',
      title: 'Brand Logo',
      subtitle: 'Upload logo',
      icon: 'fa-solid fa-image',
    },
    {
      id: 'publish',
      title: 'Publish',
      subtitle: 'SEO and final review',
      icon: 'fa-solid fa-rocket',
    },
  ];

  currentStep = signal<StepId>('basic');
  savingDraft = signal(false);
  publishing = signal(false);
  restoring = signal(true);
  brandId = signal<number | null>(null);

  // Mock dropdown data (replace with API later)
  readonly brandTypes: BrandType[] = [
    'National Brand',
    'Regional Brand',
    'Local Brand',
    'Private Label',
  ];
  categories: CategoryDto[] = [];

  categorySearch = signal('');

  // Upload state
  logoFile = signal<File | null>(null);
  logoUrl = signal<string | null>(null);

  form = this.fb.group({
    // Basic
    name: ['', [Validators.required, Validators.minLength(2)]],
    slug: ['', [Validators.required, Validators.pattern(/^[a-z0-9-]+$/)]],
    shortDescription: [''],
    brandType: ['National Brand' as BrandType],
    status: ['draft' as BrandStatus],

    // Catalog
    primaryCategoryId: [null as number | null],
    secondaryCategoryIds: [[] as number[]],
    featured: [false],
    trending: [false],
    premium: [false],
    priority: [50],
    keywords: this.fb.array<string>([]),

    // Publish settings
    accentColor: ['#2563eb'],
    customizationEnabled: [false],
    metaTitle: [''],
    metaDescription: [''],
  });

  keywordInput = signal('');

  readonly urlPreview = computed(() => {
    const slug = this.form.controls.slug.value ?? '';
    return `supidoo.com/brand/${slug || 'your-slug'}`;
  });

  readonly progressPercent = computed(() => {
    const index = this.steps.findIndex((s) => s.id === this.currentStep());
    return Math.round(((index + 1) / this.steps.length) * 100);
  });

  readonly currentStepIndex = computed(() =>
    this.steps.findIndex((s) => s.id === this.currentStep())
  );

  readonly currentStepDef = computed(
    () => this.steps[this.currentStepIndex()] ?? this.steps[0]
  );

  readonly secondaryCategoriesLabel = computed(() => {
    const list = this.selectedSecondaryCategories();
    if (!list.length) return 'Select secondary categories';
    return list.map((c) => c.name).join(', ');
  });


  readonly filteredCategories = computed(() => {
    const q = this.categorySearch().trim().toLowerCase();
    if (!q) return this.categories;
    return this.categories.filter((c) => c.name.toLowerCase().includes(q));
  });

  primaryCategoryName(): string {
    const id = this.form.controls.primaryCategoryId.value;
    return this.categories.find((c) => c.id === id)?.name ?? 'Not set';
  }

  isStepReached(stepId: StepId): boolean {
    return this.getStepIndex(stepId) <= this.currentStepIndex();
  }

  getStepIndex(stepId: StepId): number {
    return this.steps.findIndex((s) => s.id === stepId);
  }


  readonly selectedSecondaryCategories = computed(() => {
    const ids = this.form.controls.secondaryCategoryIds.value ?? [];
    return this.categories.filter((c) => ids.includes(c.id));
  });

  readonly keywords = computed(() => this.keywordsArray().value ?? []);

  constructor() {
    // Auto slug from name (only when slug is pristine/empty-ish)
    this.form.controls.name.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(120))
      .subscribe((name) => {
        const slugControl = this.form.controls.slug;
        const current = (slugControl.value ?? '').trim();
        const generated = this.slugify(name ?? '');

        if (!current || slugControl.pristine) {
          slugControl.setValue(generated, { emitEvent: false });
        }
      });

    // Autosave draft
    this.form.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(700),
        distinctUntilChanged()
      )
      .subscribe(() => this.persistDraft());

    this.restoreDraft();
    this.loadCategories();

    // Check for edit mode
    const id = this.route.snapshot.params['id'];
    if (id) {
      this.brandId.set(Number(id));
      this.loadBrand(Number(id));
    }
  }

  loadBrand(id: number) {
    this.brandService.getBrandById(id).subscribe({
      next: (brand) => {
        this.form.patchValue({
          name: brand.name,
          slug: brand.slug,
          shortDescription: brand.shortDescription || '',
          status: brand.status as BrandStatus || 'draft',
          primaryCategoryId: brand.primaryCategoryId as number,
          secondaryCategoryIds: brand.secondaryCategoryIds as number[],
          featured: !!brand.featured,
          priority: brand.priority || 50,
          accentColor: brand.accentColor || '#2563eb',
          metaTitle: brand.metaTitle || '',
          metaDescription: brand.metaDescription || '',
        });

        if (brand.logo) {
          this.logoUrl.set(brand.logo);
        }
        
        if (brand.keywords) {
          const arr = this.keywordsArray();
          arr.clear();
          brand.keywords.forEach(k => arr.push(this.fb.control(k)));
        }
      },
      error: (err) => {
        this.snackbar.error('Failed to load brand data');
        this.router.navigate(['/brands']);
      }
    });
  }

  loadCategories() {
    this.categoriesService.getRootCategories().subscribe(cats => {
      this.categories = cats;
    });
  }

  keywordsArray(): FormArray {
    return this.form.controls.keywords as FormArray;
  }

  goToStep(stepId: StepId): void {
    this.currentStep.set(stepId);
  }

  nextStep(): void {
    const i = this.steps.findIndex((s) => s.id === this.currentStep());
    if (i < this.steps.length - 1) this.currentStep.set(this.steps[i + 1].id);
  }

  prevStep(): void {
    const i = this.steps.findIndex((s) => s.id === this.currentStep());
    if (i > 0) this.currentStep.set(this.steps[i - 1].id);
  }

  // ---------- Keywords ----------
  addKeyword(): void {
    const raw = this.keywordInput().trim();
    if (!raw) return;
    const value = raw.toLowerCase();

    const arr = this.keywordsArray();
    const existing = (arr.value ?? []).map((k: string) => String(k).toLowerCase());
    if (existing.includes(value)) {
      this.keywordInput.set('');
      return;
    }
    arr.push(this.fb.control(value));
    this.keywordInput.set('');
  }

  removeKeyword(index: number): void {
    this.keywordsArray().removeAt(index);
  }

  // ---------- Category multiselect ----------
  toggleSecondaryCategory(id: number): void {
    const current = this.form.controls.secondaryCategoryIds.value ?? [];
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    this.form.controls.secondaryCategoryIds.setValue(next);
  }

  onFilesValidResult(kind: 'logo', files: ValidImageResult[]): void {
    if (!files || files.length === 0) return;
    this.logoFile.set(files[0].file);
    this.logoUrl.set(files[0].url);
  }

  removeUpload(kind: 'logo'): void {
    if (kind === 'logo') {
      this.logoFile.set(null);
      this.logoUrl.set(null);
    }
  }

  // ---------- Actions ----------
  cancel(): void {
    this.router.navigate(['/brands']);
  }

  saveDraft(): void {
    if (this.savingDraft()) return;
    this.savingDraft.set(true);
    this.persistDraft();
    setTimeout(() => {
      this.savingDraft.set(false);
      this.snackbar.success('Draft saved');
    }, 250);
  }

  publish(): void {
    if (this.publishing()) return;

    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.snackbar.error('Fix validation errors before publishing');
      this.jumpToFirstInvalidStep();
      return;
    }

    // Publish payload aligned to backend schema (uploads are UI-mocked for now)
    const name = this.form.controls.name.value!.trim();
    const slug = this.form.controls.slug.value!.trim();
    const now = new Date().toISOString();

    this.publishing.set(true);

    // Map brand type to DB-compliant snake_case values
    const rawType = (this.form.controls.brandType.value ?? 'National Brand').toLowerCase();
    let brand_type: any = 'national';
    if (rawType.includes('regional')) brand_type = 'regional';
    else if (rawType.includes('local')) brand_type = 'local';
    else if (rawType.includes('private')) brand_type = 'private_label';

    // Map status
    const rawStatus = this.form.controls.status.value;
    let status = 'draft';
    if (rawStatus === 'active') status = 'published';
    else if (rawStatus === 'hidden') status = 'archived';
    else if (rawStatus === 'draft') status = 'draft';

    const brandData: any = {
      name,
      slug,
      description: this.form.controls.shortDescription.value ?? '',
      brand_type,
      status,
      is_featured: !!this.form.controls.featured.value,
      updated_at: now
    };

    const operation = this.brandId() 
      ? this.brandService.updateBrand(this.brandId()!, brandData)
      : this.brandService.createBrand({ ...brandData, created_at: now });

    operation.subscribe({
      next: () => {
        this.snackbar.success(this.brandId() ? 'Brand updated' : 'Brand published');
        localStorage.removeItem(DRAFT_KEY);
        this.publishing.set(false);
        this.router.navigate(['/brands']);
      },
      error: (err) => {
        console.error('Failed to process brand', err);
        this.snackbar.error('Failed to save brand');
        this.publishing.set(false);
      },
    });
  }

  // ---------- Draft persistence ----------
  private persistDraft(): void {
    if (this.restoring()) return;
    const payload = {
      form: this.form.getRawValue(),
        logo: this.logoUrl(),
      step: this.currentStep(),
      savedAt: new Date().toISOString(),
    };
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
    } catch {
      // ignore
    }
  }

  private restoreDraft(): void {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) {
        this.restoring.set(false);
        return;
      }
      const parsed = JSON.parse(raw) as any;
      if (parsed?.form) {
        this.form.patchValue(parsed.form, { emitEvent: false });
        this.form.markAsPristine();
      }
      if (parsed?.step) this.currentStep.set(parsed.step as StepId);
      if (parsed?.uploads?.logo) {
        this.logoUrl.set(parsed.uploads.logo);
      }
    } catch {
      // ignore corrupt draft
    } finally {
      this.restoring.set(false);
    }
  }


  private jumpToFirstInvalidStep(): void {
    const map: Array<{ step: StepId; controls: AbstractControl[] }> = [
      {
        step: 'basic',
        controls: [
          this.form.controls.name,
          this.form.controls.slug,
          this.form.controls.brandType,
          this.form.controls.status,
        ],
      },
      { step: 'publish', controls: [this.form.controls.metaTitle] },
    ];

    for (const entry of map) {
      if (entry.controls.some((c) => c.invalid)) {
        this.currentStep.set(entry.step);
        return;
      }
    }
  }

  private slugify(input: string): string {
    return (input ?? '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 80);
  }
}
